import React, { useEffect, useMemo, useState } from "react";
import { ArrowRight, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { fetchPremiumSocial } from "../../../api/premiumSocialService";
import { useProfileHub } from "../ProfileHubContext";
import ProfileSectionCard from "../components/ProfileSectionCard";
import { API_BASE_URL } from "../../../utils/constants";

const MEDIA_BASE_URL =
  process.env.REACT_APP_MEDIA_BASE_URL || process.env.VITE_API_URL || API_BASE_URL;

function resolveThumb(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith("http")) return imagePath;
  return `${MEDIA_BASE_URL}${imagePath}`;
}

function RoundSquare({ round }) {
  const [hovered, setHovered] = useState(false);
  const thumb = resolveThumb(round.image_path);
  let cls = "flex h-7 w-7 items-center justify-center rounded-md text-[10px] transition select-none cursor-default relative ";
  let inner;
  const rank = round.answer_rank > 0 ? round.answer_rank : null;
  if (round.is_correct) {
    if (round.is_fast) {
      cls += "bg-amber-100 border border-amber-300";
      inner = <span className="text-[9px] font-black text-amber-600">⚡{rank}</span>;
    } else {
      cls += "bg-emerald-100 border border-emerald-300";
      inner = <span className="text-[11px] font-bold text-emerald-600">✓{rank && <span className="text-[8px] ml-0.5">{rank}</span>}</span>;
    }
  } else {
    cls += "bg-rose-100 border border-rose-200";
    inner = <span className="text-[11px] font-bold text-rose-400">✗</span>;
  }
  return (
    <div
      className="relative"
      onMouseEnter={() => thumb && setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={cls}>{inner}</div>
      {hovered && thumb && (
        <div className="absolute bottom-full left-1/2 z-30 mb-2 -translate-x-1/2 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl" style={{ minWidth: 100, maxWidth: 140 }}>
          <img src={thumb} alt="" className="h-16 w-full rounded-lg object-cover" />
        </div>
      )}
    </div>
  );
}

function BadgeIcon({ icon }) {
  if (!icon) return <span>*</span>;
  if (icon.startsWith("http"))
    return <img src={icon} alt="" className="h-6 w-6 object-contain" />;
  return <span>{icon}</span>;
}

function CategoryCard({ cat }) {
  const best = cat.best_game;
  return (
    <div className="rounded-2xl border border-slate-100 bg-white p-4 shadow-sm transition hover:border-slate-200 hover:shadow">
      <div className="flex items-start justify-between gap-2">
        <div className="truncate font-semibold text-pixy-ink">
          {cat.category_name || cat.category}
        </div>
        <span className="shrink-0 text-sm font-bold text-pixy-ink">
          {(cat.score || 0).toLocaleString("fr-FR")} pts
        </span>
      </div>

      {best ? (
        <div className="mt-3 rounded-xl border border-slate-100 bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-2 text-xs">
            <span className="font-semibold text-pixy-ink">
              Meilleure partie : {best.score.toLocaleString("fr-FR")} pts
            </span>
            {best.total_players > 0 ? (
              <span className="font-semibold text-sky-600 shrink-0">
                {best.rank > 0 ? `#${best.rank}` : "?"}/{best.total_players}
              </span>
            ) : (
              <span className="text-slate-400 shrink-0">{best.played_at}</span>
            )}
          </div>
          {best.rounds?.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {best.rounds.map((r, i) => (
                <RoundSquare key={i} round={r} />
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-2 text-xs text-slate-400">Pas d'historique</div>
      )}
    </div>
  );
}

export default function ProfileOverviewSection() {
  const { displayBadges, profileIdentity } = useProfileHub();
  const [friends, setFriends] = useState([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchPremiumSocial(controller.signal)
      .then((data) => {
        const accepted = (data?.friends || []).filter(
          (f) => f.status === "accepted"
        );
        setFriends(accepted);
      })
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const categoryScores = useMemo(
    () => profileIdentity.category_scores || [],
    [profileIdentity.category_scores]
  );

  return (
    <div className="space-y-6">
      {/* Stats globales */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="rounded-2xl border border-sky-100 bg-white/80 px-5 py-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-pixy-blue">
            Classement Général
          </div>
          <div className="mt-1.5 text-3xl font-bold text-pixy-ink">
            {profileIdentity.global_rank != null ? `#${profileIdentity.global_rank}` : "—"}
          </div>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-white/80 px-5 py-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-amber-600">
            Points
          </div>
          <div className="mt-1.5 text-3xl font-bold text-pixy-ink">
            {(profileIdentity.total_points || 0).toLocaleString("fr-FR")}
          </div>
        </div>
        <div className="rounded-2xl border border-slate-100 bg-white/80 px-5 py-4 shadow-sm">
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Parties jouées
          </div>
          <div className="mt-1.5 text-3xl font-bold text-pixy-ink">
            {profileIdentity.games_played ?? 0}
          </div>
        </div>
      </div>

      {/* Contenu principal : cards catégories + panneau droit */}
      <div className="grid gap-6 xl:grid-cols-[1fr,300px]">
        {/* Cards par catégorie */}
        <div className="space-y-6">
          <div>
            <div className="mb-3 text-sm font-semibold text-pixy-ink">
              Catégories jouées
            </div>
            {categoryScores.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-sm text-slate-500">
                Aucune partie comptabilisée pour le moment.
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {categoryScores.map((cat) => (
                  <CategoryCard key={cat.category} cat={cat} />
                ))}
              </div>
            )}
          </div>

        </div>

        {/* Panneau droit : amis + badges */}
        <div className="space-y-4">
          {/* Amis */}
          <ProfileSectionCard
            title={`Amis (${friends.length})`}
            action={
              <Link
                to="/profile/friends"
                className="inline-flex items-center gap-1 text-xs font-semibold text-pixy-blue hover:underline"
              >
                Voir tout
                <ArrowRight className="h-3 w-3" />
              </Link>
            }
          >
            {friends.length === 0 ? (
              <div className="space-y-3">
                <p className="text-xs text-slate-500">
                  Aucun ami pour le moment.
                </p>
                <Link
                  to="/profile/friends"
                  className="inline-flex items-center gap-2 rounded-full bg-pixy-ink px-3 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-800"
                >
                  <Users className="h-3.5 w-3.5" />
                  Gérer mes amis
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  {friends.slice(0, 10).map((f) => (
                    <Link
                      key={f.id}
                      to={`/joueur/u/${f.friend.id}`}
                      target="_blank"
                      rel="noreferrer"
                      title={f.friend.username}
                      className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-pixy-green/30 to-pixy-blue/30 text-xs font-bold text-pixy-ink ring-2 ring-white transition hover:ring-pixy-blue/40"
                    >
                      {f.friend.avatar ? (
                        <img
                          src={f.friend.avatar}
                          alt={f.friend.username}
                          className="h-9 w-9 rounded-full object-cover"
                        />
                      ) : (
                        (f.friend.username?.[0] || "?").toUpperCase()
                      )}
                    </Link>
                  ))}
                  {friends.length > 10 && (
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-500">
                      +{friends.length - 10}
                    </span>
                  )}
                </div>
                <Link
                  to="/profile/friends"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                >
                  <Users className="h-3.5 w-3.5" />
                  Gérer mes amis
                </Link>
              </div>
            )}
          </ProfileSectionCard>

          {/* Aperçu badges */}
          <ProfileSectionCard
            title="Badges"
            subtitle={`${displayBadges.length} débloqué${displayBadges.length !== 1 ? "s" : ""}`}
            action={
              <Link
                to="/profile/badges"
                className="inline-flex items-center gap-1 text-xs font-semibold text-pixy-blue hover:underline"
              >
                Voir tout
                <ArrowRight className="h-3 w-3" />
              </Link>
            }
          >
            {displayBadges.length === 0 ? (
              <p className="text-xs text-slate-500">Aucun badge débloqué.</p>
            ) : (
              <div className="space-y-2">
                {displayBadges.slice(0, 5).map((badge) => (
                  <div
                    key={badge.id}
                    className="flex items-center gap-2 rounded-xl border border-slate-100 bg-white px-3 py-2"
                  >
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-slate-50 text-sm">
                      <BadgeIcon icon={badge.icon} />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-xs font-semibold text-pixy-ink">
                        {badge.name}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ProfileSectionCard>
        </div>
      </div>
    </div>
  );
}
